import moment from "moment";

export const convertDepositsToTransactions = (deposits) => {
  return deposits.map(deposit => ({
    id: `deposit-${deposit.id}`,
    type: deposit.recordType === 'deposit' ? 'income' : 'outcome',
    source: 'booking',
    date: deposit.collectionDate,
    amount: Math.abs(deposit.depositAmount),
    currency: 'EGP',
    category: deposit.recordType === 'deposit' ? 'Booking Deposit' : 'Refund',
    description: deposit.notes || (deposit.recordType === 'deposit' ? 'Booking deposit payment' : 'Deposit refund'),
    reference: deposit.receiptId,
    paymentMethod: deposit.depositMethod,
    paymentStatus: 'paid',
    userName: deposit.userName,
    guestName: deposit.guestName,
    requestId: deposit.requestId,
    receiptId: deposit.receiptId,
    notes: deposit.notes,
    createdAt: deposit.inputDateTime
  }));
};

export const convertCostsToTransactions = (costs, categories) => {
  return costs.map(cost => {
    const category = categories.find(c => c.id === cost.categoryId);
    return {
      id: `cost-${cost.id}`,
      type: cost.type === 'income' ? 'income' : 'outcome',
      source: 'cost',
      date: cost.transactionDate,
      amount: cost.amount,
      currency: cost.currency,
      category: category?.name || 'Other',
      categoryId: cost.categoryId,
      description: cost.title,
      reference: cost.invoiceNumber || cost.receiptNumber || `COST-${cost.id}`,
      paymentMethod: cost.paymentMethod,
      paymentStatus: cost.paymentStatus,
      userName: cost.createdBy,
      vendorName: cost.vendorName,
      propertyId: cost.propertyId,
      unitId: cost.unitId,
      unitNumber: cost.unitNumber,
      invoiceNumber: cost.invoiceNumber,
      receiptId: cost.receiptNumber,
      notes: cost.notes,
      createdAt: cost.createdAt
    };
  });
};

export const mergeAndSortTransactions = (deposits, costs, categories) => {
  const depositTransactions = convertDepositsToTransactions(deposits);
  const costTransactions = convertCostsToTransactions(costs, categories);

  const allTransactions = [...depositTransactions, ...costTransactions];

  return allTransactions.sort((a, b) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
};

export const filterTransactions = (transactions, filter) => {
  return transactions.filter(transaction => {
    if (filter.searchTerm) {
      const searchLower = filter.searchTerm.toLowerCase();
      const matchesSearch =
        transaction.description.toLowerCase().includes(searchLower) ||
        transaction.userName.toLowerCase().includes(searchLower) ||
        transaction.reference.toLowerCase().includes(searchLower) ||
        transaction.category.toLowerCase().includes(searchLower) ||
        (transaction.guestName?.toLowerCase().includes(searchLower)) ||
        (transaction.vendorName?.toLowerCase().includes(searchLower)) ||
        (transaction.notes?.toLowerCase().includes(searchLower));

      if (!matchesSearch) return false;
    }

    if (filter.type && filter.type !== 'all') {
      if (transaction.type !== filter.type) return false;
    }

    if (filter.source && filter.source !== 'all') {
      if (transaction.source !== filter.source) return false;
    }

    if (filter.dateFrom && transaction.date < filter.dateFrom) return false;
    if (filter.dateTo && transaction.date > filter.dateTo) return false;

    if (filter.userName && transaction.userName !== filter.userName) return false;

    if (filter.paymentMethod && transaction.paymentMethod !== filter.paymentMethod) return false;

    if (filter.paymentStatus && transaction.paymentStatus !== filter.paymentStatus) return false;

    if (filter.categoryId && transaction.categoryId !== filter.categoryId) return false;

    if (filter.propertyId && transaction.propertyId !== filter.propertyId) return false;

    if (filter.minAmount && transaction.amount < filter.minAmount) return false;
    if (filter.maxAmount && transaction.amount > filter.maxAmount) return false;

    return true;
  });
};

export const sortTransactions = (transactions, sortBy, sortOrder) => {
  return [...transactions].sort((a, b) => {
    let aValue, bValue;

    switch (sortBy) {
      case 'amount':
        aValue = a.type === 'income' ? a.amount : -a.amount;
        bValue = b.type === 'income' ? b.amount : -b.amount;
        break;
      case 'date':
        aValue = new Date(a.date).getTime();
        bValue = new Date(b.date).getTime();
        break;
      case 'createdAt':
        aValue = new Date(a.createdAt).getTime();
        bValue = new Date(b.createdAt).getTime();
        break;
      default:
        aValue = a[sortBy];
        bValue = b[sortBy];
        break;
    }

    if (typeof aValue === 'string') {
      return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    } else {
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    }
  });
};

export const calculateTransactionStats = (transactions) => {
  const incomeTransactions = transactions.filter(t => t.type === 'income');
  const outcomeTransactions = transactions.filter(t => t.type === 'outcome');

  const totalIncome = incomeTransactions.reduce((sum, t) => sum + t.amount, 0);
  const totalOutcome = outcomeTransactions.reduce((sum, t) => sum + t.amount, 0);
  const netAmount = totalIncome - totalOutcome;

  const uniqueUsers = [...new Set(transactions.map(t => t.userName))];

  return {
    totalIncome,
    totalOutcome,
    netAmount,
    incomeTransactions: incomeTransactions.length,
    outcomeTransactions: outcomeTransactions.length,
    totalTransactions: transactions.length,
    averageIncome: incomeTransactions.length > 0 ? totalIncome / incomeTransactions.length : 0,
    averageOutcome: outcomeTransactions.length > 0 ? totalOutcome / outcomeTransactions.length : 0,
    activeUsers: uniqueUsers.length
  };
};

export const calculateMethodTotals = (transactions) => {
  const methodMap = {};

  const totalIncome = transactions.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
  const totalOutcome = transactions.filter(t => t.type === 'outcome').reduce((sum, t) => sum + t.amount, 0);

  transactions.forEach(transaction => {
    if (!methodMap[transaction.paymentMethod]) {
      methodMap[transaction.paymentMethod] = {
        incomeTotal: 0,
        outcomeTotal: 0,
        incomeCount: 0,
        outcomeCount: 0
      };
    }

    if (transaction.type === 'income') {
      methodMap[transaction.paymentMethod].incomeTotal += transaction.amount;
      methodMap[transaction.paymentMethod].incomeCount += 1;
    } else {
      methodMap[transaction.paymentMethod].outcomeTotal += transaction.amount;
      methodMap[transaction.paymentMethod].outcomeCount += 1;
    }
  });

  return Object.entries(methodMap).map(([method, data]) => ({
    method,
    incomeTotal: data.incomeTotal,
    outcomeTotal: data.outcomeTotal,
    netTotal: data.incomeTotal - data.outcomeTotal,
    incomeCount: data.incomeCount,
    outcomeCount: data.outcomeCount,
    totalCount: data.incomeCount + data.outcomeCount,
    incomePercentage: totalIncome > 0 ? (data.incomeTotal / totalIncome) * 100 : 0,
    outcomePercentage: totalOutcome > 0 ? (data.outcomeTotal / totalOutcome) * 100 : 0
  })).sort((a, b) => Math.abs(b.netTotal) - Math.abs(a.netTotal));
};

export const calculateUserTotals = (transactions) => {
  const userMap = {};

  const totalIncome = transactions.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
  const totalOutcome = transactions.filter(t => t.type === 'outcome').reduce((sum, t) => sum + t.amount, 0);

  transactions.forEach(transaction => {
    if (!userMap[transaction.userName]) {
      userMap[transaction.userName] = {
        incomeTotal: 0,
        outcomeTotal: 0,
        incomeCount: 0,
        outcomeCount: 0
      };
    }

    if (transaction.type === 'income') {
      userMap[transaction.userName].incomeTotal += transaction.amount;
      userMap[transaction.userName].incomeCount += 1;
    } else {
      userMap[transaction.userName].outcomeTotal += transaction.amount;
      userMap[transaction.userName].outcomeCount += 1;
    }
  });

  return Object.entries(userMap).map(([userName, data]) => {
    const netAmount = data.incomeTotal - data.outcomeTotal;
    const totalTransactions = data.incomeCount + data.outcomeCount;
    return {
      userName,
      totalIncome: data.incomeTotal,
      totalOutcome: data.outcomeTotal,
      netAmount,
      incomeCount: data.incomeCount,
      outcomeCount: data.outcomeCount,
      totalTransactions,
      averageTransaction: totalTransactions > 0 ? Math.abs(netAmount) / totalTransactions : 0,
      incomePercentage: totalIncome > 0 ? (data.incomeTotal / totalIncome) * 100 : 0,
      outcomePercentage: totalOutcome > 0 ? (data.outcomeTotal / totalOutcome) * 100 : 0
    };
  }).sort((a, b) => b.netAmount - a.netAmount);
};

export const calculateCategoryTotals = (transactions) => {
  const categoryMap = {};

  transactions.forEach(transaction => {
    const key = `${transaction.category}-${transaction.type}`;
    if (!categoryMap[key]) {
      categoryMap[key] = {
        type: transaction.type,
        total: 0,
        count: 0,
        categoryId: transaction.categoryId
      };
    }
    categoryMap[key].total += transaction.amount;
    categoryMap[key].count += 1;
  });

  const totalIncome = transactions.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
  const totalOutcome = transactions.filter(t => t.type === 'outcome').reduce((sum, t) => sum + t.amount, 0);

  return Object.entries(categoryMap).map(([key, data]) => {
    const category = key.substring(0, key.lastIndexOf('-'));
    const total = data.type === 'income' ? totalIncome : totalOutcome;
    return {
      category,
      categoryId: data.categoryId,
      type: data.type,
      total: data.total,
      count: data.count,
      percentage: total > 0 ? (data.total / total) * 100 : 0,
      average: data.count > 0 ? data.total / data.count : 0
    };
  }).sort((a, b) => b.total - a.total);
};

export const getDateRange = (range) => {
  const today = new Date();
  const from = new Date();
  const to = new Date();

  // Helper function to format date as YYYY-MM-DD (local timezone, not UTC)
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  switch (range) {
    case 'today':
      return {
        from: formatLocalDate(today),
        to: formatLocalDate(today)
      };
    case 'yesterday':
      from.setDate(today.getDate() - 1);
      return {
        from: formatLocalDate(from),
        to: formatLocalDate(from)
      };
    case 'last7Days':
      from.setDate(today.getDate() - 7);
      return {
        from: formatLocalDate(from),
        to: formatLocalDate(today)
      };
    case 'last30Days':
      from.setDate(today.getDate() - 30);
      return {
        from: formatLocalDate(from),
        to: formatLocalDate(today)
      };
    case 'thisMonth':
      from.setDate(1);
      to.setMonth(today.getMonth() + 1);
      to.setDate(0);
      return {
        from: formatLocalDate(from),
        to: formatLocalDate(to)
      };
    case 'lastMonth':
      from.setMonth(today.getMonth() - 1);
      from.setDate(1);
      to.setDate(0);
      return {
        from: formatLocalDate(from),
        to: formatLocalDate(to)
      };
    case 'thisYear':
      from.setMonth(0);
      from.setDate(1);
      return {
        from: formatLocalDate(from),
        to: formatLocalDate(today)
      };
    default:
      return { from: '', to: '' };
  }
};

export const generateTransactionExport = (transactions, stats, methodTotals, userTotals, categoryTotals) => {
  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  xml += '<IncomeOutcomeReport>\n';
  xml += '  <Summary>\n';
  xml += `    <TotalIncome>${stats.totalIncome.toFixed(2)}</TotalIncome>\n`;
  xml += `    <TotalOutcome>${stats.totalOutcome.toFixed(2)}</TotalOutcome>\n`;
  xml += `    <NetAmount>${stats.netAmount.toFixed(2)}</NetAmount>\n`;
  xml += `    <IncomeTransactions>${stats.incomeTransactions}</IncomeTransactions>\n`;
  xml += `    <OutcomeTransactions>${stats.outcomeTransactions}</OutcomeTransactions>\n`;
  xml += `    <TotalTransactions>${stats.totalTransactions}</TotalTransactions>\n`;
  xml += `    <ActiveUsers>${stats.activeUsers}</ActiveUsers>\n`;
  xml += `    <GeneratedAt>${new Date().toISOString()}</GeneratedAt>\n`;
  xml += '  </Summary>\n';

  xml += '  <PaymentMethodTotals>\n';
  methodTotals.forEach(method => {
    xml += '    <Method>\n';
    xml += `      <Name>${method.method}</Name>\n`;
    xml += `      <IncomeTotal>${method.incomeTotal.toFixed(2)}</IncomeTotal>\n`;
    xml += `      <OutcomeTotal>${method.outcomeTotal.toFixed(2)}</OutcomeTotal>\n`;
    xml += `      <NetTotal>${method.netTotal.toFixed(2)}</NetTotal>\n`;
    xml += `      <TotalCount>${method.totalCount}</TotalCount>\n`;
    xml += '    </Method>\n';
  });
  xml += '  </PaymentMethodTotals>\n';

  xml += '  <UserTotals>\n';
  userTotals.forEach(user => {
    xml += '    <User>\n';
    xml += `      <Name>${user.userName}</Name>\n`;
    xml += `      <TotalIncome>${user.totalIncome.toFixed(2)}</TotalIncome>\n`;
    xml += `      <TotalOutcome>${user.totalOutcome.toFixed(2)}</TotalOutcome>\n`;
    xml += `      <NetAmount>${user.netAmount.toFixed(2)}</NetAmount>\n`;
    xml += `      <TotalTransactions>${user.totalTransactions}</TotalTransactions>\n`;
    xml += '    </User>\n';
  });
  xml += '  </UserTotals>\n';

  xml += '  <CategoryTotals>\n';
  categoryTotals.forEach(category => {
    xml += '    <Category>\n';
    xml += `      <Name>${category.category}</Name>\n`;
    xml += `      <Type>${category.type}</Type>\n`;
    xml += `      <Total>${category.total.toFixed(2)}</Total>\n`;
    xml += `      <Count>${category.count}</Count>\n`;
    xml += `      <Percentage>${category.percentage.toFixed(2)}</Percentage>\n`;
    xml += '    </Category>\n';
  });
  xml += '  </CategoryTotals>\n';

  xml += '  <Transactions>\n';
  transactions.forEach(transaction => {
    xml += '    <Transaction>\n';
    xml += `      <ID>${transaction.id}</ID>\n`;
    xml += `      <Type>${transaction.type}</Type>\n`;
    xml += `      <Source>${transaction.source}</Source>\n`;
    xml += `      <Date>${transaction.date}</Date>\n`;
    xml += `      <Amount>${transaction.amount.toFixed(2)}</Amount>\n`;
    xml += `      <Currency>${transaction.currency}</Currency>\n`;
    xml += `      <Category>${transaction.category}</Category>\n`;
    xml += `      <Description>${transaction.description}</Description>\n`;
    xml += `      <Reference>${transaction.reference}</Reference>\n`;
    xml += `      <PaymentMethod>${transaction.paymentMethod}</PaymentMethod>\n`;
    xml += `      <PaymentStatus>${transaction.paymentStatus}</PaymentStatus>\n`;
    xml += `      <UserName>${transaction.userName}</UserName>\n`;
    if (transaction.guestName) xml += `      <GuestName>${transaction.guestName}</GuestName>\n`;
    if (transaction.vendorName) xml += `      <VendorName>${transaction.vendorName}</VendorName>\n`;
    xml += '    </Transaction>\n';
  });
  xml += '  </Transactions>\n';
  xml += '</IncomeOutcomeReport>';

  return xml;
};
